import logger from '@ayocore/platform/common/helpers/logger';
import type { AwaitableRpcClient } from '@ayocore/platform/server/rpc/types';
import type { DealEventRepository } from '../../../../server/repositories/DealEventRepository';
import type { DealEvent } from '../../../../schemas/deal-event.schema';
import type { Task } from '@ayocore/tasks/schemas/task.schema';
import type { TaskRepository } from '@ayocore/tasks/server/repositories/TaskRepository';
import type { TaskStatusRepository } from '@ayocore/tasks/server/repositories/TaskStatusRepository';
import type { Comment } from '@ayocore/comments/schemas/comment.schema';
import type { CommentRepository } from '@ayocore/comments/server/repositories/CommentRepository';

export interface DealEventState {
  events: DealEvent[];
  comments: Comment[];
  activeTasks: Task[];
  loading: boolean;
  error: string | null;
}

export class DealEventController {
  private state: DealEventState = {
    events: [],
    comments: [],
    activeTasks: [],
    loading: true,
    error: null,
  };
  private listeners: Set<() => void> = new Set();

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  getSnapshot = (): DealEventState => {
    return this.state;
  };

  private notify(): void {
    this.listeners.forEach(listener => listener());
  }

  private setState(updater: Partial<DealEventState>): void {
    this.state = { ...this.state, ...updater };
    this.notify();
  }

  private async fetchData(
    dealId: number,
    dealUuid: string,
    deps: {
      dealEventRepo: AwaitableRpcClient<DealEventRepository>;
      commentRepo: AwaitableRpcClient<CommentRepository>;
      taskRepo: AwaitableRpcClient<TaskRepository>;
      taskStatusRepo: AwaitableRpcClient<TaskStatusRepository>;
    }
  ): Promise<{ events: DealEvent[]; comments: Comment[]; activeTasks: Task[] }> {
    const { dealEventRepo, commentRepo, taskRepo, taskStatusRepo } = deps;
    const [events, comments, activeTasks] = await Promise.all([
      dealEventRepo.getTimelineWithRelations(dealId),
      this.loadComments(dealUuid, commentRepo),
      this.loadActiveTasks(dealUuid, taskRepo, taskStatusRepo),
    ]);
    return { events, comments, activeTasks };
  }

  async load(
    dealId: number,
    dealUuid: string,
    deps: {
      dealEventRepo: AwaitableRpcClient<DealEventRepository>;
      commentRepo: AwaitableRpcClient<CommentRepository>;
      taskRepo: AwaitableRpcClient<TaskRepository>;
      taskStatusRepo: AwaitableRpcClient<TaskStatusRepository>;
    }
  ): Promise<void> {
    this.setState({ loading: true, error: null });
    try {
      const { events, comments, activeTasks } = await this.fetchData(dealId, dealUuid, deps);
      this.setState({
        events,
        comments,
        activeTasks,
        loading: false,
        error: null,
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      logger.error('[DealEventController] Failed to load events', { dealId, error: errorMessage });
      this.setState({
        events: [],
        comments: [],
        activeTasks: [],
        loading: false,
        error: errorMessage,
      });
    }
  }

  private async loadActiveTasks(
    dealUuid: string,
    taskRepo: AwaitableRpcClient<TaskRepository>,
    taskStatusRepo: AwaitableRpcClient<TaskStatusRepository>
  ): Promise<Task[]> {
    if (!dealUuid) return [];

    const activeStatuses = await taskStatusRepo
      .where('is_active', true)
      .where('is_final', false)
      .fetch();

    const statusIds = activeStatuses.map(s => s.id);
    if (statusIds.length === 0) return [];

    return taskRepo
      .where('entity_uuid', dealUuid)
      .where('entity_type', 'crm::DealRepository')
      .whereIn('task_status_id', statusIds)
      .orderBy('created_at', 'desc')
      .fetch();
  }

  private async loadComments(
    dealUuid: string,
    commentRepo: AwaitableRpcClient<CommentRepository>
  ): Promise<Comment[]> {
    if (!dealUuid) {
      return [];
    }

    return commentRepo.getEntityComments(dealUuid, 'crm::DealRepository');
  }

  async reload(
    dealId: number,
    dealUuid: string,
    deps: {
      dealEventRepo: AwaitableRpcClient<DealEventRepository>;
      commentRepo: AwaitableRpcClient<CommentRepository>;
      taskRepo: AwaitableRpcClient<TaskRepository>;
      taskStatusRepo: AwaitableRpcClient<TaskStatusRepository>;
    }
  ): Promise<void> {
    try {
      const { events, comments, activeTasks } = await this.fetchData(dealId, dealUuid, deps);
      this.setState({ events, comments, activeTasks, error: null });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      logger.error('[DealEventController] Failed to reload events', {
        dealId,
        error: errorMessage,
      });
      this.setState({
        events: [],
        comments: [],
        activeTasks: [],
        error: errorMessage,
      });
    }
  }

  clear(): void {
    this.setState({
      events: [],
      comments: [],
      activeTasks: [],
      loading: true,
      error: null,
    });
  }
}
